// ============================================================
// NotificationLogPage.js — worklist D15 (2026-08-16)
// The notification DELIVERY trail, finally readable in the portal.
//
// NotificationLog was write-only from Phase 2: inserted by the senders,
// updated by the ACS delivery webhook, read by nothing — so diagnosing
// "did the email actually go?" meant App Insights KQL or live SQL (it
// took a 3-day platform crypto outage and a month of false "delivery
// failed" banners to notice). This page is the read.
//
// The byStatus chips honour the client/channel/window filters but NEVER
// the status filter (server-enforced) — narrowing the rows to 'failed'
// must not make the failure count look like the whole story.
//
// Read-only by design: there is nothing to mutate on a delivery record.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Badge from '../components/Badge';
import DataTable from '../components/DataTable';
import { listNotificationLog } from '../api/adminNotificationLog';
import { listAdminClients } from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { formatDateTime } from '../utils/format';

const CHANNEL_OPTIONS = [
  { value: '', label: 'All channels' },
  { value: 'email', label: 'Email' },
  { value: 'teams', label: 'Teams' },
  { value: 'in_app', label: 'In-app' },
  { value: 'internal_email', label: 'Internal email' },
  { value: 'internal_teams', label: 'Internal Teams' },
];

const STATUS_OPTIONS = [
  '', 'pending', 'sent', 'delivered', 'failed', 'bounced', 'suppressed', 'quarantined', 'filtered_spam', 'expanded',
];

const DAYS_OPTIONS = [1, 7, 30, 90];

const STATUS_VARIANT = {
  delivered: 'success', sent: 'success', expanded: 'success',
  pending: 'neutral', suppressed: 'neutral',
  failed: 'danger', bounced: 'danger', quarantined: 'warning', filtered_spam: 'warning',
};

const PAGE_SIZE = 25;

export default function NotificationLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') || '';
  const channel = searchParams.get('channel') || '';
  const status = searchParams.get('status') || '';
  const days = searchParams.get('days') || '7';

  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listNotificationLog({
        clientId: clientId || undefined, channel: channel || undefined,
        status: status || undefined, days: Number(days),
      });
      setData(res); setPage(1);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId, channel, status, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Client names for the filter — best-effort; the table already carries
  // each row's ClientName from the server join.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listAdminClients({ pageSize: 200 });
        if (!cancelled) setClients(res.items || res.clients || []);
      } catch { /* the filter degrades to All clients */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const rows = (data && data.rows) || [];
  const paged = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const byStatus = (data && data.byStatus) || {};

  const columns = [
    { key: 'createdAt', label: 'When', width: '170px', render: (r) => formatDateTime(r.createdAt) },
    { key: 'clientName', label: 'Client', render: (r) => r.clientName || `Client ${r.clientId}` },
    { key: 'channel', label: 'Channel', width: '110px', render: (r) => r.channel },
    { key: 'recipientAddress', label: 'Recipient', render: (r) => r.recipientAddress },
    { key: 'subject', label: 'Subject', render: (r) => r.subject || '—' },
    {
      key: 'status', label: 'Status', width: '120px',
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{r.status}</Badge>,
    },
    { key: 'failureReason', label: 'Failure reason', render: (r) => r.failureReason || '—' },
  ];

  return (
    <div>
      <PageHeader
        title="Notification Delivery"
        subtitle="The per-recipient delivery trail - what was sent, on which channel, and what the provider reported back."
      />

      <div style={{ display: 'flex', gap: 'var(--space-3, 12px)', flexWrap: 'wrap', margin: '0 0 12px' }}>
        <select aria-label="Filter by client" value={clientId} onChange={(e) => setParam('clientId', e.target.value)}>
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.clientId} value={c.clientId}>{c.displayName || c.clientName}</option>
          ))}
        </select>
        <select aria-label="Filter by channel" value={channel} onChange={(e) => setParam('channel', e.target.value)}>
          {CHANNEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select aria-label="Filter by status" value={status} onChange={(e) => setParam('status', e.target.value)}>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || 'All statuses'}</option>)}
        </select>
        <select aria-label="Window in days" value={days} onChange={(e) => setParam('days', e.target.value)}>
          {DAYS_OPTIONS.map((d) => <option key={d} value={String(d)}>{`Last ${d} day${d > 1 ? 's' : ''}`}</option>)}
        </select>
      </div>

      {/* The chips: window-wide truth regardless of the status filter. */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '0 0 12px' }}>
        {Object.entries(byStatus).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
          <Badge key={s} variant={STATUS_VARIANT[s] || 'neutral'}>{`${s}: ${n}`}</Badge>
        ))}
      </div>

      {data && data.truncated ? (
        <p style={{ margin: '0 0 12px', color: 'var(--color-text-muted, #555)' }}>
          Showing the most recent {rows.length} rows for this window - older rows are not listed. Narrow the filters or shorten the window.
        </p>
      ) : null}

      <DataTable
        data={paged}
        columns={columns}
        rowKey={(r) => r.notificationLogId}
        totalCount={rows.length}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyMessage="No notifications in this window."
      />
    </div>
  );
}
